/**
 * THE MARKET SCANNER's client side: the run states, the filter pills, and the row model.
 *
 * <p>Pure. No React, no axios — so the filtering and the label arithmetic can be tested without
 * mounting a dialog or standing up a backend, which is where every interesting decision here
 * lives.
 */

/** The live execution states, in the order a run passes through them. */
export const SCAN_STATE = {
  IDLE: 'idle',
  LOADING: 'loading',
  SCANNING: 'scanning',
  PERSISTING: 'persisting',
  COMPLETE: 'complete',
  FAILED: 'failed',
};

/**
 * What each state says on screen.
 *
 * <p>⚠ THE MIDDLE THREE ARE NOT OBSERVED, and the labels do not pretend otherwise. The scan is
 * ONE synchronous request, so the client cannot see the backend move from loading bars to
 * scanning to persisting — it sees a request in flight and then a result. Rather than animate a
 * fake progression, the states exist as a vocabulary the UI steps through at the points it
 * genuinely knows about, and the scanning label says what the server is doing rather than
 * claiming to track it.
 */
export const SCAN_STATE_LABEL = {
  [SCAN_STATE.IDLE]: '',
  [SCAN_STATE.LOADING]: 'Loading symbols and band periods…',
  [SCAN_STATE.SCANNING]: 'Scanning PTA and PTB levels across the market…',
  [SCAN_STATE.PERSISTING]: 'Writing results to Firestore…',
  [SCAN_STATE.COMPLETE]: 'Complete.',
  [SCAN_STATE.FAILED]: 'The scan failed.',
};

/** The two batch runs the scanner supports. */
export const SCAN_MARKETS = [
  { value: 'INDIA', label: 'India (NSE/BSE)' },
  { value: 'US', label: 'US (equities, indices, forex, metals)' },
];

/**
 * The filter pills, as specified.
 *
 * <p>Each is a PREDICATE rather than a string the table matches on, so "Bullish Bounces" means
 * the signal types that are bullish bounces rather than a label somebody has to keep in step
 * with the backend's enum.
 */
export const SCANNER_FILTERS = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'india', label: 'India', test: (r) => r.market === 'INDIA' },
  { id: 'us', label: 'US', test: (r) => r.market === 'US' },
  {
    id: 'bounces',
    label: 'Bullish Bounces',
    test: (r) => hasType(r, 'REVERSAL_SUPPORT'),
  },
  {
    id: 'rejections',
    label: 'Bearish Rejections',
    test: (r) => hasType(r, 'REVERSAL_RESISTANCE'),
  },
  {
    id: 'breakouts',
    label: 'Breakouts',
    // Both directions. A "breakout" pill that showed only the bullish one would leave
    // CROSS_DOWN reachable from no pill at all — the one outcome a filter set must not have.
    test: (r) => hasType(r, 'CROSS_UP') || hasType(r, 'CROSS_DOWN'),
  },
];

/** Every signal on a row, both engines, in ladder order. */
export function allSignals(row) {
  if (!row) return [];
  return [
    ...(Array.isArray(row.ptaSignals) ? row.ptaSignals.map((s) => ({ ...s, engine: 'PTA' })) : []),
    ...(Array.isArray(row.ptbSignals) ? row.ptbSignals.map((s) => ({ ...s, engine: 'PTB' })) : []),
  ];
}

function hasType(row, type) {
  return allSignals(row).some((s) => s && s.signalType === type);
}

/** Apply one pill. An unknown id shows everything rather than nothing. */
export function applyFilter(rows, filterId) {
  const filter = SCANNER_FILTERS.find((f) => f.id === filterId);
  const list = Array.isArray(rows) ? rows : [];
  return filter ? list.filter(filter.test) : list;
}

/**
 * The "Triggered Level" cell, e.g. `PTA T2 (1820.50)`.
 *
 * <p>The FIRST signal, with a count when there are more. A row can carry several — that is what
 * earns it a HIGH tier — and listing them all would make the column unreadable at a glance,
 * while showing one and saying nothing would hide that the others exist.
 */
export function triggeredLevelLabel(row) {
  const signals = allSignals(row);
  if (signals.length === 0) return '—';
  const first = signals[0];
  const price = Number.isFinite(first.levelPrice) ? first.levelPrice.toFixed(2) : '—';
  const head = `${first.engine} ${first.lineName} (${price})`;
  return signals.length > 1 ? `${head} +${signals.length - 1}` : head;
}

/** Human wording for the action column, keyed on the enum rather than re-derived. */
export const SIGNAL_ACTION_LABEL = {
  CROSS_UP: 'Bullish Breakout',
  CROSS_DOWN: 'Bearish Breakdown',
  REVERSAL_RESISTANCE: 'Bearish Rejection',
  REVERSAL_SUPPORT: 'Bullish Rebound',
};

/** The action cell: the first signal's wording, or the raw enum if one is ever added. */
export function signalActionLabel(row) {
  const signals = allSignals(row);
  if (signals.length === 0) return '—';
  const type = signals[0].signalType;
  // Falls back to the enum rather than to a blank: a type with no label here is a backend value
  // this file has not caught up with, and showing it is how that gets noticed.
  return SIGNAL_ACTION_LABEL[type] || type;
}

/** Is the row's leading signal bullish? Drives the row's colour. */
export function isBullish(row) {
  const signals = allSignals(row);
  if (signals.length === 0) return null;
  const type = signals[0].signalType;
  if (type === 'CROSS_UP' || type === 'REVERSAL_SUPPORT') return true;
  if (type === 'CROSS_DOWN' || type === 'REVERSAL_RESISTANCE') return false;
  return null;
}

/** HIGH first, then symbol — the order the backend already sorted into, restated for safety. */
export const TIER_RANK = { HIGH: 0, MEDIUM: 1, WATCH: 2 };

export function sortCandidates(rows) {
  return [...(Array.isArray(rows) ? rows : [])].sort((a, b) => {
    const byTier = (TIER_RANK[a.confluenceTier] ?? 9) - (TIER_RANK[b.confluenceTier] ?? 9);
    if (byTier !== 0) return byTier;
    return String(a.symbol).localeCompare(String(b.symbol));
  });
}

/**
 * The one-line summary of a finished run.
 *
 * <p>Every number it quotes is a decision or a failure, so none of them is silent — and
 * `firestoreAvailable: false` is stated rather than left to be inferred from a zero, because a
 * scan that computed correctly and stored nothing looks identical to one that found nothing.
 */
export function describeScan(report) {
  if (!report) return '';
  if (report.ok === false) return report.error || 'The scan failed.';
  const parts = [
    `${report.market} ${report.scanDate}`,
    `${report.symbolsScanned} scanned`,
  ];
  // ⚠ JUDGED, not scanned, is the denominator "0 with signals" has to be read against.
  // A scan of a date the data does not reach judges NOTHING and used to report
  // "386 scanned · 0 with signals" — the same sentence a quiet market produces.
  if (typeof report.symbolsJudged === 'number'
      && report.symbolsJudged !== report.symbolsScanned) {
    parts.push(`${report.symbolsJudged} judged`);
  }
  parts.push(`${report.symbolsWithSignals} with signals`);
  if (report.symbolsWithoutBar) parts.push(`${report.symbolsWithoutBar} had no bar on that date`);
  if (report.symbolsWithoutLines) parts.push(`${report.symbolsWithoutLines} had no lines in force`);
  // Every reason, so the counts RECONCILE: judged + no bar + no lines + no history == scanned.
  // One unexplained symbol is one a reader has to guess about.
  if (report.symbolsWithoutHistory) {
    parts.push(`${report.symbolsWithoutHistory} had too little history`);
  }
  if (report.symbolsFailed) parts.push(`${report.symbolsFailed} failed`);
  if (report.firestoreAvailable === false) {
    parts.push('NOT persisted — Firestore is not configured');
  } else if (typeof report.persisted === 'number') {
    parts.push(`${report.persisted} persisted`);
  }
  if (typeof report.elapsedMillis === 'number') {
    parts.push(`${(report.elapsedMillis / 1000).toFixed(1)}s`);
  }
  return parts.join(' · ');
}
